import SlIcon from "@shoelace-style/shoelace/dist/react/icon";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { updatePrioridadThunk } from "../../reduxSlices/studentSlice";
import store, { useAppDispatch } from "../../app/store";
import { getCuposAlumnos } from "../../reduxAsyncThunks/subjects.thunks";
import { useToastSuccess, useToastWarning } from "../../hooks/useToast";
import {
  selectFlags,
  selectOfertaInvalidandoOVenciendo,
  setBannerCountdownVisible,
  setBannerPrioridadVisible,
} from "../../reduxSlices/globalFlagsSlice";
import { selectStudent } from "../../reduxSlices/studentSlice";
import { useTranslatedText } from "../../contexts/TranslationContext";
import { parseArtDateTimeToMillis } from "../../utils/prioridadClock";

const RETRY_AFTER_PREMATURE_MS = 2000;

interface TimeLeft {
  days?: number;
  hours?: number;
  minutes?: number;
  seconds?: number;
  completed: boolean;
}

/** true si la hora de servidor recibida ya pasó F_HASTA — ventana de prioridad cerrada */
function isVentanaPrioridadVencida(fHasta: string | undefined, serverNowMs: number): boolean {
  const hastaMs = parseArtDateTimeToMillis(fHasta);
  if (hastaMs === null) return false;
  return serverNowMs > hastaMs;
}

function FormattedText({ text }: { text: string }) {
  const formatText = (text: string) => {
    const parts = text.split(/([^\s]+\s+\d{1,2}\s+de\s+[^\s]+\s+del\s+\d{4})/g);

    return parts.map((part, index) => {
      if (/\w+\s+\d{1,2}\s+de\s+\w+\s+del\s+\d{4}/.test(part)) {
        return <strong key={index}>{part}</strong>;
      }
      return part;
    });
  };

  return <span>{formatText(text)}</span>;
}

const SimpleCountdownRenderer = ({ days, hours, minutes, seconds, completed }: TimeLeft) => {
  if (completed) {
    return null;
  }

  const parts = [];
  if (days && days > 0) parts.push(`${days}d`);
  if (hours && hours > 0) parts.push(`${hours}h`);
  if (minutes && minutes > 0) parts.push(`${minutes}m`);
  if (seconds && seconds > 0) parts.push(`${seconds}s`);

  // Si no hay partes de tiempo para mostrar, no renderizar nada
  if (parts.length === 0) {
    return null;
  }

  return (
    <span
      className="text-xl font-bold text-gray-800 whitespace-nowrap relative pt-4 md:pt-0 md:pl-4
  before:content-[''] before:absolute before:top-[10px] before:left-1/2 before:-translate-x-1/2 
  before:w-[100%] w-full before:h-[1px] before:bg-[#F5A800]
  md:before:top-1/2 md:before:-translate-y-1/2 md:before:left-0 md:before:translate-x-0
  md:before:w-[1px] md:before:h-full"
    >
      {parts.join(" ")}
    </span>
  );
};

export default function BannerConsultaPrioridad() {
  const dispatch = useAppDispatch();
  const showWarning = useToastWarning();
  const showSuccess = useToastSuccess();
  const [timeLeft, setTimeLeft] = useState<TimeLeft>({ completed: true });
  const [hasExecutedCallback, setHasExecutedCallback] = useState(false);
  const [hadActiveCountdown, setHadActiveCountdown] = useState(false);
  const { prioridad, mensajePrioridad, fechasPrioridad, mostrarCountdownPrioridad, serverClockOffsetMs } =
    useSelector(selectStudent);
  const translatedMensajePrioridad = useTranslatedText(mensajePrioridad);
  const { bannerCountdownVisible } = useSelector(selectFlags);
  const isUpdatingPrioridad = useRef(false);
  const prioridadRef = useRef<boolean | null>(null);
  const fechasPrioridadRef = useRef(fechasPrioridad);
  const hadActiveCountdownRef = useRef(false);
  const serverClockOffsetRef = useRef(serverClockOffsetMs);
  const retryTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Mantener los refs actualizados con los valores actuales
  useEffect(() => {
    prioridadRef.current = prioridad;
  }, [prioridad]);

  useEffect(() => {
    fechasPrioridadRef.current = fechasPrioridad;
  }, [fechasPrioridad]);

  useEffect(() => {
    hadActiveCountdownRef.current = hadActiveCountdown;
  }, [hadActiveCountdown]);

  useEffect(() => {
    serverClockOffsetRef.current = serverClockOffsetMs;
  }, [serverClockOffsetMs]);

  const fDesde = fechasPrioridad?.F_DESDE ?? "";

  const targetMs = useMemo(() => parseArtDateTimeToMillis(fDesde), [fDesde]);

  /**
   * Hora de servidor estimada, recalculada en cada llamada como reloj local + offset medido al
   * recibir la última respuesta. Al no anclarse a `performance.now()`, un desmontaje por navegación
   * o una suspensión del sistema ya no desplazan el contador.
   */
  const serverNowMs = useCallback(() => Date.now() + serverClockOffsetRef.current, []);

  // Si cambia el F_DESDE (nuevo turno), reiniciar flags de countdown
  useEffect(() => {
    if (!fDesde) return;
    setHasExecutedCallback(false);
    setHadActiveCountdown(false);
  }, [fDesde]);

  // Usar refs para las funciones de toast para evitar re-creaciones
  const showWarningRef = useRef(showWarning);
  const showSuccessRef = useRef(showSuccess);

  useEffect(() => {
    showWarningRef.current = showWarning;
    showSuccessRef.current = showSuccess;
  }, [showWarning, showSuccess]);

  const updatePrioridadOnRefresh = useCallback(async () => {
    // Evitar ejecuciones duplicadas simultáneas
    if (isUpdatingPrioridad.current) {
      console.log("🔄 BannerConsultaPrioridad: updatePrioridadOnRefresh ya en curso, omitiendo...");
      return;
    }

    const fechasActuales = fechasPrioridadRef.current;
    if (isVentanaPrioridadVencida(fechasActuales?.F_HASTA, serverNowMs())) {
      console.log("🔄 BannerConsultaPrioridad: ventana de prioridad vencida, no se consulta de nuevo");
      return;
    }

    isUpdatingPrioridad.current = true;
    try {
      // Usar los refs para obtener los valores actuales sin incluir en las dependencias
      const resultAction = await dispatch(updatePrioridadThunk(prioridadRef.current));

      if (updatePrioridadThunk.fulfilled.match(resultAction)) {
        const response = resultAction.payload;
        if (!response.tienePrioridad) {
          // Respuesta recién recibida: su hora de servidor es la referencia más precisa disponible.
          const serverNowDeLaRespuesta =
            parseArtDateTimeToMillis(response.fechasPrioridad?.fechaInicioContador) ?? serverNowMs();
          const vencida = isVentanaPrioridadVencida(response.fechasPrioridad?.F_HASTA, serverNowDeLaRespuesta);

          if (vencida) {
            // Ventana cerrada: no toast de "aún no comenzó" ni retry
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
              retryTimeoutRef.current = null;
            }
          } else {
            showWarningRef.current("Aún no comenzó tu turno de inscripción.");
            // Prematuro (skew): reintentar en 2s sin quedar bloqueado
            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
            }
            // El remaining ya es <= 0; basta con liberar el one-shot para que el próximo tick reintente.
            retryTimeoutRef.current = setTimeout(() => {
              setHasExecutedCallback(false);
              retryTimeoutRef.current = null;
            }, RETRY_AFTER_PREMATURE_MS);
          }
        } else {
          // Usar el ref para verificar si había un countdown activo
          if (hadActiveCountdownRef.current) {
            showSuccessRef.current("Ya podés inscribirte.", "¡Excelente!");
          }
        }
        const studentId = store.getState().student.id;
        const { getCuposAlumnoFetched, getCuposAlumnoInFlight } = store.getState().subjects;
        const cuposYaSolicitados = getCuposAlumnoFetched || getCuposAlumnoInFlight;
        const ofertaRegenerandose = selectOfertaInvalidandoOVenciendo(store.getState());
        if (studentId && !cuposYaSolicitados && !ofertaRegenerandose) {
          console.log("🔄 BannerConsultaPrioridad: se consulta cupos");
          await dispatch(getCuposAlumnos(studentId));
        }
        dispatch(setBannerPrioridadVisible(true));
      } else {
        // El thunk fue rechazado
        const errorPayload = resultAction.payload;
        if (errorPayload === "REINTENTOS_AGOTADOS") {
          showWarningRef.current(
            "No se pudo obtener la información de prioridad después de varios intentos. La página se refrescará automáticamente.",
          );
          setTimeout(() => {
            window.location.reload();
          }, 3500);
        } else {
          showWarningRef.current("Error al consultar el estado de prioridad. Intenta nuevamente.");
          setHasExecutedCallback(false);
        }
      }
    } catch (error) {
      console.error("❌ Error al actualizar prioridad:", error);
      showWarningRef.current("Error al consultar el estado de prioridad. Intenta nuevamente.");
      setHasExecutedCallback(false);
    } finally {
      isUpdatingPrioridad.current = false;
    }
  }, [dispatch, serverNowMs]); // Ambos estables

  const handleBannerPrioridadRef = useRef<() => void>();
  handleBannerPrioridadRef.current = updatePrioridadOnRefresh;

  const getRemainingMs = useCallback(() => {
    if (targetMs === null) return null;
    return targetMs - serverNowMs();
  }, [targetMs, serverNowMs]);

  const updateTimer = useCallback(() => {
    const remaining = getRemainingMs();
    if (remaining === null) {
      return;
    }

    if (remaining <= 0) {
      setTimeLeft({ completed: true });

      // Usar prioridadRef.current en lugar de prioridad para evitar re-creaciones
      if (prioridadRef.current || hasExecutedCallback) {
        return;
      }
      setHasExecutedCallback(true);

      const fechas = fechasPrioridadRef.current;
      if (isVentanaPrioridadVencida(fechas?.F_HASTA, serverNowMs())) {
        return;
      }

      // Usar el ref para llamar a la función sin incluirla en las dependencias
      handleBannerPrioridadRef.current?.();
      return;
    }

    setTimeLeft({
      days: Math.floor(remaining / (1000 * 60 * 60 * 24)),
      hours: Math.floor((remaining % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
      minutes: Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60)),
      seconds: Math.floor((remaining % (1000 * 60)) / 1000),
      completed: false,
    });
  }, [getRemainingMs, hasExecutedCallback, serverNowMs]);

  useEffect(() => {
    if (targetMs === null) {
      setTimeLeft({ completed: true });
      return;
    }

    const remaining = getRemainingMs();
    if (remaining !== null && remaining > 0) {
      setHadActiveCountdown(true);
    }

    updateTimer();
    const interval = setInterval(updateTimer, 1000);
    return () => clearInterval(interval);
  }, [targetMs, getRemainingMs, updateTimer]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        updateTimer();
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, [updateTimer]);

  useEffect(() => {
    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
    };
  }, []);

  const handleCloseBannerPrioridad = (value: boolean) => {
    dispatch(setBannerCountdownVisible(value));
  };

  return (
    <div
      className={`border-[1px] ${
        !prioridad ? "border-[#F5A800] bg-[#FFF7E4]" : "border-[#00C4B3] bg-[#ECF4F4]"
      } rounded-md flex justify-between items-center gap-[15px] px-4 py-2 min-h-[48px] mb-1 md:mb-0 ${
        bannerCountdownVisible ? "" : "hidden"
      }`}
    >
      <div
        className={`${
          !prioridad ? "bg-[#F5A800]" : "bg-[#00C4B3]"
        } min-w-[26px] min-h-[26px] flex items-center justify-center rounded-full`}
      >
        <svg xmlns="http://www.w3.org/2000/svg" width="3" height="15" viewBox="0 0 3 15" fill="none">
          <path
            d="M1.74457 4.12639C2.36431 4.12639 2.8667 4.62879 2.8667 5.24852L2.8667 13.8112C2.8667 14.431 2.36431 14.9333 1.74457 14.9333V14.9333C1.12484 14.9333 0.622446 14.431 0.622446 13.8112L0.622446 5.24852C0.622447 4.62879 1.12484 4.12639 1.74457 4.12639V4.12639ZM1.74457 0.814363C2.35814 0.814363 2.8667 1.31175 2.8667 1.92532V1.92532C2.8667 2.53888 2.35814 3.03627 1.74457 3.03627V3.03627C1.13101 3.03627 0.622447 2.53888 0.622447 1.92532V1.92532C0.622447 1.31175 1.13101 0.814363 1.74457 0.814363V0.814363Z"
            fill="white"
          />
        </svg>
      </div>
      <div className="flex flex-col md:flex-row justify-between w-full md:gap-[15px] items-center">
        <p className="text-[13px] md:text-[14px] leading-4">
          <FormattedText text={translatedMensajePrioridad} />
        </p>
        {mostrarCountdownPrioridad && !timeLeft.completed && (
          <div key={fDesde || "no-date"} className={`flex items-center gap-[15px] w-full md:w-auto`}>
            <SimpleCountdownRenderer {...timeLeft} />
          </div>
        )}
      </div>
      <SlIcon
        className="close-mobile-size min-w-[16px]"
        name="x-lg"
        onClick={() => handleCloseBannerPrioridad(false)}
        style={{ cursor: "pointer" }}
      ></SlIcon>
    </div>
  );
}
